import React, { useState, useEffect } from 'react';
import { getApiBaseUrl } from '../../services/api';
import Pagination from '../../components/common/Pagination';

function GlobalUsersPage() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('');
    const [companies, setCompanies] = useState([]);

    // 페이지네이션 상태
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    useEffect(() => {
        fetchUsers();
    }, [page, size, filter]);

    useEffect(() => {
        fetchCompanies();
    }, []);

    const fetchCompanies = async () => {
        try {
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const response = await fetch(`${getApiBaseUrl()}/platform/companies?size=1000`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            const result = await response.json();
            if (result.success && result.data) {
                const companyNames = [...new Set((result.data.content || []).map(c => c.companyName))];
                setCompanies(companyNames);
            }
        } catch (error) {
            console.error('회사 목록 조회 실패:', error);
        }
    };

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            let url = `${getApiBaseUrl()}/platform/users?page=${page}&size=${size}`;

            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            const result = await response.json();
            if (result.success && result.data) {
                const data = result.data;
                let content = data.content || [];

                // 프론트엔드에서 회사 필터링 적용
                if (filter) {
                    content = content.filter(u => u.companyName === filter);
                }

                setUsers(content);
                setTotalElements(data.totalElements || 0);
                setTotalPages(data.totalPages || 0);
            }
        } catch (error) {
            console.error('사용자 목록 조회 실패:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString('ko-KR');
    };

    const getRoleBadgeClass = (role) => {
        switch (role) {
            case 'SUPER_ADMIN': return 'active';
            case 'ADMIN': return 'pending';
            default: return '';
        }
    };

    const getRoleLabel = (role) => {
        switch (role) {
            case 'SUPER_ADMIN': return '슈퍼관리자';
            case 'ADMIN': return '관리자';
            case 'USER': return '일반사용자';
            default: return role;
        }
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handleSizeChange = (newSize) => {
        setSize(newSize);
        setPage(0);
    };

    const handleFilterChange = (e) => {
        setFilter(e.target.value);
        setPage(0);
    };

    return (
        <div className="platform-page">
            <div className="platform-page-header">
                <h1 className="platform-page-title">전체 사용자</h1>
                <p className="platform-page-description">모든 회사의 사용자를 조회합니다.</p>
            </div>

            {/* 통계 */}
            <div className="platform-stats-grid">
                <div className="platform-stat-card">
                    <div className="platform-stat-icon">
                        <span className="material-symbols-outlined">group</span>
                    </div>
                    <div className="platform-stat-info">
                        <div className="platform-stat-value">{totalElements}</div>
                        <div className="platform-stat-label">전체 사용자</div>
                    </div>
                </div>
                <div className="platform-stat-card">
                    <div className="platform-stat-icon">
                        <span className="material-symbols-outlined">admin_panel_settings</span>
                    </div>
                    <div className="platform-stat-info">
                        <div className="platform-stat-value">
                            {users.filter(u => u.role === 'SUPER_ADMIN' || u.role === 'ADMIN').length}
                        </div>
                        <div className="platform-stat-label">관리자</div>
                    </div>
                </div>
                <div className="platform-stat-card">
                    <div className="platform-stat-icon">
                        <span className="material-symbols-outlined">check_circle</span>
                    </div>
                    <div className="platform-stat-info">
                        <div className="platform-stat-value">
                            {users.filter(u => u.status === 'ACTIVE').length}
                        </div>
                        <div className="platform-stat-label">활성 사용자</div>
                    </div>
                </div>
            </div>

            {/* 사용자 목록 */}
            <div className="platform-table-container">
                <div className="platform-table-header">
                    <h3 className="platform-table-title">사용자 목록</h3>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <select
                            value={filter}
                            onChange={handleFilterChange}
                            style={{
                                padding: '8px 12px',
                                borderRadius: '6px',
                                border: '1px solid #2a2f38',
                                background: '#21252b',
                                color: '#E5E7EB',
                                fontSize: '13px'
                            }}
                        >
                            <option value="">전체 회사</option>
                            {companies.map(company => (
                                <option key={company} value={company}>{company}</option>
                            ))}
                        </select>
                    </div>
                </div>
                {loading ? (
                    <div className="platform-empty">
                        <div className="platform-empty-text">로딩 중...</div>
                    </div>
                ) : users.length === 0 ? (
                    <div className="platform-empty">
                        <span className="material-symbols-outlined platform-empty-icon">group</span>
                        <div className="platform-empty-text">사용자가 없습니다.</div>
                    </div>
                ) : (
                    <>
                        <table className="platform-table">
                            <thead>
                                <tr>
                                    <th>사용자명</th>
                                    <th>이메일</th>
                                    <th>소속 회사</th>
                                    <th>권한</th>
                                    <th>상태</th>
                                    <th>최근 로그인</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((user) => (
                                    <tr key={user.id}>
                                        <td><strong>{user.username}</strong></td>
                                        <td>{user.email}</td>
                                        <td>{user.companyName}</td>
                                        <td>
                                            <span className={`platform-badge ${getRoleBadgeClass(user.role)}`}>
                                                {getRoleLabel(user.role)}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`platform-badge ${user.status === 'ACTIVE' ? 'active' : 'inactive'}`}>
                                                {user.status === 'ACTIVE' ? '활성' : '비활성'}
                                            </span>
                                        </td>
                                        <td>{formatDate(user.lastLoginAt)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <Pagination
                            page={page}
                            totalPages={totalPages}
                            totalElements={totalElements}
                            size={size}
                            onPageChange={handlePageChange}
                            onSizeChange={handleSizeChange}
                        />
                    </>
                )}
            </div>
        </div>
    );
}

export default GlobalUsersPage;
